"use client";

import { SURFACE } from "@commerce/components/page";
import { Time } from "@commerce/components/time";
import { orpc } from "@shared/lib/orpc";
import {
	useQuery,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@ui/components/input-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { Switch } from "@ui/components/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ui/components/table";
import { Tabs, TabsList, TabsTrigger } from "@ui/components/tabs";
import { cn } from "@ui/lib";
import {
	ChevronRightIcon,
	CircleCheckIcon,
	ScrollTextIcon,
	SearchIcon,
} from "lucide-react";
import { useDeferredValue, useId, useMemo, useState } from "react";
import { LogSheet } from "./LogSheet";
import {
	groupRows,
	LIVE_POLL_MS,
	type LogRow,
	type LogsSearch,
	logErrorsQuery,
	logsQuery,
	OUTCOME_META,
	OUTCOMES,
	resultOf,
	SOURCE_LABEL,
	SOURCE_TAB_LABEL,
	SOURCE_TABS,
	WINDOW_LABEL,
	WINDOWS,
} from "./meta";

const ALL = "all";
export const DEFAULT_WINDOW_HOURS = 24;

/**
 * Developer logs (spec 5.6): errors first, grouped by Meta code, then every exchange with Meta
 * and the order system, filterable by source, status, number and id, with one row's detail in
 * a sheet. The /logs page and a number's Activity tab (`connectionId` fixed) share it.
 */
export function LogsView({
	organizationId,
	search,
	onSearch,
	connectionId,
}: {
	organizationId: string;
	search: LogsSearch;
	onSearch: (patch: Partial<LogsSearch>) => void;
	/** The number's Activity tab: its rows only, and no number filter. */
	connectionId?: string;
}) {
	const filters = useMemo(
		() => ({
			source: search.source,
			outcome: search.outcome,
			number: connectionId ?? search.number,
			code: search.code,
			q: search.q,
		}),
		[
			search.source,
			search.outcome,
			search.number,
			search.code,
			search.q,
			connectionId,
		],
	);
	// Deferred so a filter change keeps the old rows on screen while the new ones load.
	const deferred = useDeferredValue(filters);
	const query = useSuspenseInfiniteQuery({
		...logsQuery(organizationId, deferred),
		refetchInterval: search.live ? LIVE_POLL_MS : false,
	});
	const rows = query.data.pages.flatMap((page) => page.logs);
	const groups = groupRows(rows);
	const filtered = Boolean(
		filters.source ||
			filters.outcome ||
			(!connectionId && filters.number) ||
			filters.code !== undefined ||
			filters.q,
	);
	const clear = () =>
		onSearch({
			source: undefined,
			outcome: undefined,
			number: undefined,
			code: undefined,
			q: undefined,
		});

	return (
		<div className="flex flex-col gap-5">
			<ErrorsStrip
				organizationId={organizationId}
				hours={search.hours ?? DEFAULT_WINDOW_HOURS}
				connectionId={connectionId}
				activeCode={search.code}
				live={Boolean(search.live)}
				onPick={(code) =>
					onSearch({
						code:
							code === search.code
								? undefined
								: (code ?? undefined),
						outcome: "FAILED",
					})
				}
				onHours={(hours) => onSearch({ hours })}
				onLive={(live) => onSearch({ live: live || undefined })}
			/>

			<div className="flex flex-col gap-3">
				<Tabs
					value={search.source ?? ALL}
					onValueChange={(value) =>
						onSearch({
							source:
								value === ALL
									? undefined
									: (value as (typeof SOURCE_TABS)[number]),
						})
					}
				>
					<TabsList
						variant="line"
						className="w-full justify-start overflow-x-auto border-b no-scrollbar"
					>
						<TabsTrigger value={ALL} className="flex-none px-3">
							All
						</TabsTrigger>
						{SOURCE_TABS.map((source) => (
							<TabsTrigger
								key={source}
								value={source}
								className="flex-none px-3"
							>
								{SOURCE_TAB_LABEL[source]}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
				<Filters
					organizationId={organizationId}
					search={search}
					onSearch={onSearch}
					showNumber={!connectionId}
					filtered={filtered}
					onClear={clear}
				/>
			</div>

			<div
				className={cn(
					"transition-opacity duration-(--duration-dropdown)",
					deferred !== filters && "opacity-60",
				)}
				aria-busy={deferred !== filters}
			>
				{groups.length === 0 ? (
					filtered ? (
						<Empty className="rounded-3xl border">
							<EmptyHeader>
								<EmptyTitle>
									Nothing matches these filters
								</EmptyTitle>
							</EmptyHeader>
							<EmptyContent>
								<Button variant="outline" onClick={clear}>
									Show every row
								</Button>
							</EmptyContent>
						</Empty>
					) : (
						<Empty className="rounded-3xl border">
							<EmptyHeader>
								<EmptyMedia variant="icon">
									<ScrollTextIcon />
								</EmptyMedia>
								<EmptyTitle>No traffic yet</EmptyTitle>
								<EmptyDescription>
									Every message in or out, checkout form
									request and call step shows here once a
									number is connected.
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					)
				) : (
					<LogRows
						groups={groups}
						onOpen={(row) => onSearch({ id: row.id })}
					/>
				)}
			</div>

			{query.hasNextPage ? (
				<Button
					variant="outline"
					className="self-center"
					onClick={() => query.fetchNextPage()}
					disabled={query.isFetchingNextPage}
				>
					{query.isFetchingNextPage ? (
						<Spinner data-icon="inline-start" />
					) : null}
					Show older rows
				</Button>
			) : null}

			<LogSheet
				organizationId={organizationId}
				logId={search.id ?? null}
				onClose={() => onSearch({ id: undefined })}
				onOpen={(id) => onSearch({ id })}
			/>
		</div>
	);
}

/** "131047 Window closed ×12 · 131026 Undeliverable ×3", each a filter. */
function ErrorsStrip({
	organizationId,
	hours,
	connectionId,
	activeCode,
	live,
	onPick,
	onHours,
	onLive,
}: {
	organizationId: string;
	hours: number;
	connectionId?: string;
	activeCode: number | undefined;
	live: boolean;
	onPick: (code: number | null) => void;
	onHours: (hours: number | undefined) => void;
	onLive: (live: boolean) => void;
}) {
	const id = useId();
	const { data } = useSuspenseQuery({
		...logErrorsQuery(organizationId, hours, connectionId),
		refetchInterval: live ? LIVE_POLL_MS : false,
	});
	const total = data.groups.reduce((sum, group) => sum + group.count, 0);
	return (
		<section
			aria-labelledby={`${id}-title`}
			className={cn(SURFACE, "flex flex-col gap-3 p-4 md:p-5")}
		>
			<div className="flex flex-wrap items-center justify-between gap-3">
				<h2
					id={`${id}-title`}
					className="flex items-center gap-2 font-semibold text-base tracking-tight"
				>
					Errors
					<Badge
						variant={total ? "destructive" : "success"}
						className="tabular"
					>
						{total.toLocaleString("en-US")}
					</Badge>
				</h2>
				<div className="flex items-center gap-3">
					<Select
						items={Object.fromEntries(
							WINDOWS.map((value) => [
								String(value),
								WINDOW_LABEL[value],
							]),
						)}
						value={String(hours)}
						onValueChange={(value) =>
							onHours(
								Number(value) === DEFAULT_WINDOW_HOURS
									? undefined
									: Number(value),
							)
						}
					>
						<SelectTrigger
							size="sm"
							aria-label="Errors over"
							className="w-36"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{WINDOWS.map((value) => (
								<SelectItem key={value} value={String(value)}>
									{WINDOW_LABEL[value]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<label
						htmlFor={`${id}-live`}
						className="flex items-center gap-2 text-sm"
					>
						<Switch
							id={`${id}-live`}
							checked={live}
							onCheckedChange={onLive}
						/>
						Live
					</label>
				</div>
			</div>
			{data.groups.length === 0 ? (
				<p className="flex items-center gap-2 text-muted-foreground text-sm">
					<CircleCheckIcon
						className="size-4 text-success"
						aria-hidden
					/>
					No errors in this window.
				</p>
			) : (
				<ul className="flex flex-wrap gap-2">
					{data.groups.map((group) => {
						const active =
							group.errorCode !== null &&
							group.errorCode === activeCode;
						return (
							<li
								key={`${group.errorCode ?? "none"}-${group.title}`}
							>
								<button
									type="button"
									aria-pressed={active}
									onClick={() => onPick(group.errorCode)}
									className={cn(
										"flex max-w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-left text-sm ring-1 ring-foreground/8 outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/30",
										active && "bg-muted ring-brand",
									)}
								>
									{group.errorCode !== null ? (
										<span className="font-mono text-xs tabular">
											{group.errorCode}
										</span>
									) : null}
									<span className="min-w-0 truncate">
										{group.title}
									</span>
									<span className="shrink-0 font-medium text-destructive text-xs tabular">
										×{group.count.toLocaleString("en-US")}
									</span>
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</section>
	);
}

function Filters({
	organizationId,
	search,
	onSearch,
	showNumber,
	filtered,
	onClear,
}: {
	organizationId: string;
	search: LogsSearch;
	onSearch: (patch: Partial<LogsSearch>) => void;
	showNumber: boolean;
	filtered: boolean;
	onClear: () => void;
}) {
	const id = useId();
	// What's typed, kept against the search it was typed over: any new search (submitted,
	// cleared or opened from a link) replaces it with its own text.
	const [draft, setDraft] = useState<{
		q: string | undefined;
		text: string;
	} | null>(null);
	const text = draft && draft.q === search.q ? draft.text : (search.q ?? "");
	const numbers =
		useQuery({
			...orpc.channels.whatsapp.list.queryOptions({
				input: { organizationId },
			}),
			enabled: showNumber,
			retry: false,
			select: (list) => list.numbers,
		}).data ?? [];
	const statusItems = {
		[ALL]: "Any status",
		...Object.fromEntries(OUTCOMES.map((o) => [o, OUTCOME_META[o].label])),
	};
	const numberItems = {
		[ALL]: "Every number",
		...Object.fromEntries(
			numbers.map((n) => [
				n.id,
				n.label || n.displayPhone || n.phoneNumberId,
			]),
		),
	};

	return (
		<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
			<Select
				items={statusItems}
				value={search.outcome ?? ALL}
				onValueChange={(value) =>
					onSearch({
						outcome:
							value === ALL || !value
								? undefined
								: (value as (typeof OUTCOMES)[number]),
					})
				}
			>
				<SelectTrigger
					size="sm"
					aria-label="Status"
					className="w-full sm:w-40"
				>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{Object.entries(statusItems).map(([value, label]) => (
						<SelectItem key={value} value={value}>
							{label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{showNumber && numbers.length > 1 ? (
				<Select
					items={numberItems}
					value={search.number ?? ALL}
					onValueChange={(value) =>
						onSearch({
							number:
								value === ALL || !value
									? undefined
									: String(value),
						})
					}
				>
					<SelectTrigger
						size="sm"
						aria-label="Number"
						className="w-full sm:w-48"
					>
						<SelectValue className="truncate" />
					</SelectTrigger>
					<SelectContent>
						{Object.entries(numberItems).map(([value, label]) => (
							<SelectItem key={value} value={value}>
								{label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			) : null}
			<form
				className="sm:min-w-64 sm:flex-1"
				onSubmit={(event) => {
					event.preventDefault();
					onSearch({ q: text.trim() || undefined });
				}}
			>
				<label htmlFor={`${id}-q`} className="sr-only">
					Search by message, call, order or conversation id
				</label>
				<InputGroup className="h-8">
					<InputGroupAddon>
						<SearchIcon />
					</InputGroupAddon>
					<InputGroupInput
						id={`${id}-q`}
						value={text}
						onChange={(event) =>
							setDraft({ q: search.q, text: event.target.value })
						}
						onBlur={() => onSearch({ q: text.trim() || undefined })}
						placeholder="wamid, call id, order or conversation"
						className="font-mono text-xs placeholder:font-sans"
					/>
				</InputGroup>
			</form>
			{filtered ? (
				<Button variant="ghost" size="sm" onClick={onClear}>
					Clear
				</Button>
			) : null}
		</div>
	);
}

function LogRows({
	groups,
	onOpen,
}: {
	groups: LogRow[][];
	onOpen: (row: LogRow) => void;
}) {
	return (
		<>
			<div className={cn(SURFACE, "max-lg:hidden")}>
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead className="pl-5">Time</TableHead>
							<TableHead>Source</TableHead>
							<TableHead>What</TableHead>
							<TableHead>Result</TableHead>
							<TableHead>Shopper</TableHead>
							<TableHead className="w-10 pr-5">
								<span className="sr-only">Open</span>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{groups.map((group) => {
							const row = group[0] as LogRow;
							return (
								<TableRow
									key={row.id}
									className="cursor-pointer"
									onClick={() => onOpen(row)}
								>
									<TableCell className="py-2.5 pl-5 text-muted-foreground text-xs tabular whitespace-nowrap">
										<Time at={new Date(row.createdAt)} />
									</TableCell>
									<TableCell className="py-2.5 text-sm whitespace-nowrap">
										{SOURCE_LABEL[row.source]}
									</TableCell>
									<TableCell className="max-w-md py-2.5">
										{/* The row is clickable; the button is the keyboard and screen reader way in. */}
										<button
											type="button"
											onClick={(event) => {
												event.stopPropagation();
												onOpen(row);
											}}
											className="flex min-w-0 max-w-full flex-col items-start rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
										>
											<span className="max-w-full truncate font-mono text-xs">
												{row.type}
												{group.length > 1 ? (
													<span className="ml-2 font-sans text-muted-foreground">
														+{group.length - 1}{" "}
														related
													</span>
												) : null}
											</span>
											{row.summary || row.errorTitle ? (
												<span className="max-w-full truncate text-muted-foreground text-xs">
													{row.errorTitle ??
														row.summary}
												</span>
											) : null}
										</button>
									</TableCell>
									<TableCell className="py-2.5 whitespace-nowrap">
										<span className="flex items-center gap-2">
											<Badge
												variant={
													OUTCOME_META[row.outcome]
														.variant
												}
											>
												{
													OUTCOME_META[row.outcome]
														.label
												}
											</Badge>
											<span className="text-muted-foreground text-xs tabular">
												{resultOf(row)}
											</span>
										</span>
									</TableCell>
									<TableCell
										className="max-w-40 truncate py-2.5 text-left text-sm"
										dir="auto"
									>
										{row.shopperName}
									</TableCell>
									<TableCell className="py-2.5 pr-5">
										<ChevronRightIcon
											className="size-4 text-muted-foreground"
											aria-hidden
										/>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</div>
			<ul className={cn(SURFACE, "divide-y divide-border/70 lg:hidden")}>
				{groups.map((group) => {
					const row = group[0] as LogRow;
					return (
						<li key={row.id}>
							<button
								type="button"
								onClick={() => onOpen(row)}
								className="flex w-full items-start gap-3 px-4 py-3 text-left outline-none focus-visible:bg-muted"
							>
								<span className="flex min-w-0 flex-1 flex-col gap-1">
									<span className="flex items-center gap-2">
										<Badge
											variant={
												OUTCOME_META[row.outcome]
													.variant
											}
										>
											{OUTCOME_META[row.outcome].label}
										</Badge>
										<span className="truncate text-sm">
											{SOURCE_LABEL[row.source]}
										</span>
										<Time
											at={new Date(row.createdAt)}
											className="ml-auto shrink-0 text-muted-foreground text-xs tabular"
										/>
									</span>
									<span className="truncate font-mono text-xs">
										{row.type}
										{group.length > 1 ? (
											<span className="ml-2 font-sans text-muted-foreground">
												+{group.length - 1} related
											</span>
										) : null}
									</span>
									<span className="flex min-w-0 items-center gap-2 text-muted-foreground text-xs">
										<span className="tabular">
											{resultOf(row)}
										</span>
										{row.shopperName ? (
											<span
												className="truncate"
												dir="auto"
											>
												· {row.shopperName}
											</span>
										) : null}
									</span>
								</span>
								<ChevronRightIcon
									className="mt-1 size-4 shrink-0 text-muted-foreground"
									aria-hidden
								/>
							</button>
						</li>
					);
				})}
			</ul>
		</>
	);
}

/** Shaped like the screen: the errors strip, the tabs, then rows. */
export function LogsSkeleton() {
	return (
		<div className="flex flex-col gap-5" aria-busy>
			<div className={cn(SURFACE, "flex flex-col gap-3 p-4 md:p-5")}>
				<div className="flex items-center justify-between">
					<Skeleton className="h-5 w-24" />
					<Skeleton className="h-8 w-48" />
				</div>
				<div className="flex flex-wrap gap-2">
					<Skeleton className="h-8 w-56 rounded-xl" />
					<Skeleton className="h-8 w-44 rounded-xl" />
				</div>
			</div>
			<Skeleton className="h-8 w-full max-w-2xl" />
			<div className="flex gap-2">
				<Skeleton className="h-8 w-40" />
				<Skeleton className="h-8 flex-1" />
			</div>
			<div
				className={cn(
					SURFACE,
					"flex flex-col divide-y divide-border/70",
				)}
			>
				{Array.from({ length: 8 }).map((_, i) => (
					<div key={i} className="flex items-center gap-4 px-5 py-3">
						<Skeleton className="h-4 w-20" />
						<Skeleton className="h-4 w-24 max-sm:hidden" />
						<Skeleton className="h-4 flex-1" />
						<Skeleton className="h-5 w-20 rounded-full" />
					</div>
				))}
			</div>
		</div>
	);
}
